import React from "react";
import dayjs from "dayjs";
import { Box, Icon, useBreakpointValue } from "@chakra-ui/react";
import { MdStar, MdPublic, MdTimer, MdAccessTime, MdDirectionsCar, MdUpdate, MdCalendarToday } from "react-icons/md";
import DetailRow from "./DetailRow";
import { renderCitiesSection, renderMaterialsSection, renderThematicsSection } from "./detailSections";

/**
 * Converts a value using the given unit (mock implementation).
 * @param {*} val - The value to convert
 * @param {*} unit - The unit to apply
 * @returns {*} The converted value
 */
const convertUnit = (val, unit) => val;

/**
 * Decodes a label format for display, optionally adapted for artist context (mock implementation).
 * @param {*} val - The value to decode
 * @param {boolean} isArtist - Whether the context is an artist musician
 * @returns {*} The decoded label
 */
const labelFormatDecode = (val, isArtist) => val;

/**
 * Displays full details for a service including rating, country, timing,
 * cities, materials, thematics, transport, and dates.
 * @param {Object} props
 * @param {Object} props.service - The service data object
 * @param {string} props.currency - Whether to use currency symbol
 * @param {Object} props.serviceCountry - Country info with currencies
 * @returns {React.ReactElement}
 */
function Details({ service, currency, serviceCountry }) {
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const isArtist = service?.artist_type === 'artist_musician';

    return (
        <Box>
            <DetailsBasicInfo service={service} isArtist={isArtist} isMobile={isMobile} />
            {renderCitiesSection(service, isMobile)}
            {renderMaterialsSection(service, isMobile)}
            {renderThematicsSection(service, isMobile)}
            <DetailsFooter service={service} currency={currency} serviceCountry={serviceCountry} isMobile={isMobile} />
        </Box>
    );
}

/**
 * Renders the basic info rows: rating, country, preparation time, and duration.
 * @param {Object} props
 * @param {Object} props.service - The service data object
 * @param {boolean} props.isArtist - Whether the service belongs to an artist musician
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function DetailsBasicInfo({ service, isArtist, isMobile }) {
    return (
        <>
            <DetailRow
                icon={<Icon as={MdStar} color="rgba(255, 193, 7, 1)" boxSize="16px" />}
                label="Note"
                value={service?.notes || "Aucune note"}
                tooltip="Note moyenne du service"
                isMobile={isMobile}
            />
            <DetailRow
                icon={<Icon as={MdPublic} color="rgba(33, 150, 243, 1)" boxSize="16px" />}
                label="Pays"
                value={service?.country || "Non defini"}
                tooltip="Pays ou le service est propose"
                isMobile={isMobile}
            />
            <DetailRow
                icon={<Icon as={MdTimer} color="rgba(156, 39, 176, 1)" boxSize="16px" />}
                label="Temps de preparation"
                value={formatPreparationTime(service, isArtist)}
                tooltip="Temps necessaire pour preparer le service"
                isMobile={isMobile}
            />
            <DetailRow
                icon={<Icon as={MdAccessTime} color="rgba(255, 152, 0, 1)" boxSize="16px" />}
                label="Duree de la prestation"
                value={formatServiceDuration(service, isArtist)}
                tooltip="Duree totale du service"
                isMobile={isMobile}
            />
        </>
    );
}

/**
 * Renders the footer rows: transport expenses, modification date, and creation date.
 * @param {Object} props
 * @param {Object} props.service - The service data object
 * @param {string} props.currency - Whether to use currency symbol
 * @param {Object} props.serviceCountry - Country info with currencies
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function DetailsFooter({ service, currency, serviceCountry, isMobile }) {
    return (
        <>
            <DetailRow
                icon={<Icon as={MdDirectionsCar} color="rgba(0, 188, 212, 1)" boxSize="16px" />}
                label="Frais de transport"
                value={formatTravelExpenses(service, currency, serviceCountry)}
                tooltip="Cout du deplacement"
                isMobile={isMobile}
            />
            <DetailRow
                icon={<Icon as={MdUpdate} color="rgba(255, 193, 7, 1)" boxSize="16px" />}
                label="Derniere modification"
                value={dayjs(service?.modified_at).format("DD/MM/YYYY")}
                tooltip="Date de la derniere mise a jour"
                isMobile={isMobile}
            />
            <DetailRow
                icon={<Icon as={MdCalendarToday} color="rgba(76, 175, 80, 1)" boxSize="16px" />}
                label="Cree le"
                value={dayjs(service?.created_at).format("DD/MM/YYYY")}
                tooltip="Date de creation du service"
                isMobile={isMobile}
            />
        </>
    );
}

/**
 * Formats the preparation time label from service data.
 * @param {Object} service - The service data object
 * @param {boolean} isArtist - Whether the service belongs to an artist musician
 * @returns {string} Formatted preparation time or fallback text
 */
function formatPreparationTime(service, isArtist) {
    if (!service?.preparation_time) return "Non defini";
    return labelFormatDecode(
        convertUnit(service.preparation_time, service.preparation_time),
        isArtist
    );
}

/**
 * Formats the service duration label from service data.
 * @param {Object} service - The service data object
 * @param {boolean} isArtist - Whether the service belongs to an artist musician
 * @returns {string} Formatted duration or fallback text
 */
function formatServiceDuration(service, isArtist) {
    if (!service?.duration_of_the_service) return "Non defini";
    return labelFormatDecode(
        convertUnit(service.duration_of_the_service, service.unit_duration_of_the_service),
        isArtist
    );
}

/**
 * Formats travel expenses into a display string.
 * @param {Object} service - The service data object
 * @param {string} currency - Whether to use currency symbol
 * @param {Object} serviceCountry - Country info with currencies
 * @returns {string} Formatted travel expenses text
 */
function formatTravelExpenses(service, currency, serviceCountry) {
    const symbol = currency ? serviceCountry?.currencies?.symbol : serviceCountry;
    if (!service?.travel_expenses?.from) return "Gratuit";
    if (service?.travel_expenses?.to) {
        return `Entre ${service.travel_expenses.from} ${symbol} et ${service.travel_expenses.to} ${symbol}`;
    }
    return `${service.travel_expenses.from} ${symbol}`;
}

export default Details;
